import React from 'react'
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  Grid,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material'
import { StadsprognoseFormData, StadsprognoseConfig } from '../../../types/stadsprognoseTypes'

interface Props {
  formData: StadsprognoseFormData
  onChange: (updates: Partial<StadsprognoseFormData>) => void
  config: StadsprognoseConfig | null
}

const aannameOptions = [
  { value: '3', label: 'Gemiddelde laatste 3 jaren' },
  { value: '5', label: 'Gemiddelde laatste 5 jaren' },
  { value: '10', label: 'Gemiddelde laatste 10 jaren' },
  { value: 'all', label: 'Gemiddelde van alle jaren' },
  { value: 'last', label: 'Laatste Jaar' },
]

const correctieStkOptions = [
  { value: 0, label: 'Overnemen van de stuurkaart' },
  { value: 1, label: 'Volg jaarlijkse ontwikkeling CBS' },
  { value: 2, label: 'Volg leeftijd specifieke jaarlijkse ontwikkeling CBS' },
]

const Step4BepalenStuurkaart: React.FC<Props> = ({ formData, onChange, config }) => {
  const hasMatrixOptions = (config?.matrix_codes?.length ?? 0) > 0

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Bepalen Stuurkaart</Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Alle velden (m.u.v. basis stuurkaart) zijn verplicht.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl>
            <FormLabel>Selecteer welke TFRN</FormLabel>
            <RadioGroup
              row
              value={formData.correctie_tfrn}
              onChange={(e) => onChange({ correctie_tfrn: e.target.value })}
            >
              <FormControlLabel value="cbs" control={<Radio />} label="CBS (NL)" />
              <FormControlLabel value="gemeente" control={<Radio />} label="Gemeente" />
              {hasMatrixOptions && (
                <FormControlLabel value="stuurkaart" control={<Radio />} label="Stuurkaart" />
              )}
            </RadioGroup>
          </FormControl>
        </Grid>

        {formData.correctie_tfrn === 'gemeente' && (
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Selecteer aanname voor berekening TFRN (bij Gemeente)</InputLabel>
              <Select
                value={formData.aanname_tfrn}
                label="Selecteer aanname voor berekening TFRN (bij Gemeente)"
                onChange={(e) => onChange({ aanname_tfrn: e.target.value })}
              >
                {aannameOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {hasMatrixOptions && (
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Selecteer een basis Stuurkaart</InputLabel>
              <Select
                value={formData.selected_matrix_code}
                label="Selecteer een basis Stuurkaart"
                onChange={(e) => onChange({ selected_matrix_code: e.target.value })}
              >
                <MenuItem value="">
                  <em>(selecteer een code)</em>
                </MenuItem>
                {config?.matrix_codes?.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {`${item.code}: ${item.naam ?? item.code}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel>Selecteer correctie sterftekans</InputLabel>
            <Select
              value={formData.correctie_stk}
              label="Selecteer correctie sterftekans"
              onChange={(e) => onChange({ correctie_stk: Number(e.target.value) })}
            >
              {correctieStkOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl>
            <FormLabel>Gebruik berekende sexratio</FormLabel>
            <RadioGroup
              row
              value={formData.correctie_sexratio ? 'ja' : 'nee'}
              onChange={(e) => onChange({ correctie_sexratio: e.target.value === 'ja' })}
            >
              <FormControlLabel value="ja" control={<Radio />} label="Ja" />
              <FormControlLabel value="nee" control={<Radio />} label="Nee" />
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl>
            <FormLabel>Correctie Salut met CBS Buitenlands Migratie Saldo</FormLabel>
            <RadioGroup
              row
              value={formData.correctie_salut ? 'ja' : 'nee'}
              onChange={(e) => onChange({ correctie_salut: e.target.value === 'ja' })}
            >
              <FormControlLabel value="ja" control={<Radio />} label="Ja" />
              <FormControlLabel value="nee" control={<Radio />} label="Nee" />
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl>
            <FormLabel>Bereken correctie factor bewoon</FormLabel>
            <RadioGroup
              row
              value={formData.correctie_fbewwon ? 'ja' : 'nee'}
              onChange={(e) => onChange({ correctie_fbewwon: e.target.value === 'ja' })}
            >
              <FormControlLabel value="ja" control={<Radio />} label="Ja" />
              <FormControlLabel value="nee" control={<Radio />} label="Nee" />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  )
}

export default Step4BepalenStuurkaart
